import { clsx } from 'clsx'

import { Rows3Icon } from '@buffer-mono/popcorn'

import type {
  Image,
  Link,
} from '~publish/legacy/composer/composer/entities/factories'
import { cleanUrl } from '~publish/legacy/composer/composer/utils/StringUtils'

import styles from './LinkPreview.module.css'

import type { JSX } from 'react'

const Url = ({
  url,
  clickable,
}: {
  url: string
  clickable: boolean
}): JSX.Element => (
  <a
    className={clsx(styles.url, 'url', {
      [styles.urlNotClickable]: !clickable,
    })}
    href={url}
    target="_blank"
    rel="noopener noreferrer"
  >
    {cleanUrl(url)}
  </a>
)

const LinkSection = ({
  url,
  title,
  titleOnTop = false,
  clickable,
}: {
  url: string
  title: string | null
  titleOnTop: boolean
  clickable: boolean
}): JSX.Element => {
  if (titleOnTop) {
    return (
      <>
        <p className={clsx(styles.title, 'title')}>{title}</p>
        <Url url={url} clickable={clickable} />
      </>
    )
  }

  return (
    <>
      <Url url={url} clickable={clickable} />
      <p className={clsx(styles.title, 'title')}>{title}</p>
    </>
  )
}

const CompactView = ({
  url,
  title,
  titleOnTop = false,
  clickable,
  description,
}: {
  url: string
  title: string | null
  titleOnTop: boolean
  clickable: boolean
  description?: string | null
}): JSX.Element => (
  <div className={styles.compactBox}>
    <LinkSection
      url={url}
      title={title}
      titleOnTop={titleOnTop}
      clickable={clickable}
    />
    {description && <p className={styles.description}>{description}</p>}
  </div>
)

const FullView = ({
  thumbnail,
  url,
  title,
  titleOnTop = false,
  clickable,
  imageCover,
  showEmptyThumbnail,
  description,
}: {
  thumbnail: Image | null
  url: string
  title: string | null
  titleOnTop: boolean
  clickable: boolean
  imageCover: boolean
  showEmptyThumbnail: boolean
  description?: string | null
}): JSX.Element => (
  <>
    {thumbnail && (
      <img
        className={clsx(styles.image, { [styles.imageCover]: imageCover })}
        src={thumbnail.url}
        alt={thumbnail.altText || ''}
      />
    )}
    {!thumbnail && showEmptyThumbnail && (
      <div className={styles.emptyImage}>
        <Rows3Icon size="large" />
      </div>
    )}
    <div className={clsx(styles.content, 'content')}>
      <LinkSection
        url={url}
        title={title}
        titleOnTop={titleOnTop}
        clickable={clickable}
      />
      {description && (
        <p className={clsx(styles.description, 'description')}>{description}</p>
      )}
    </div>
  </>
)

const LinkPreview = ({
  link,
  titleOnTop = false,
  imageCover = false,
  showEmptyThumbnail = false,
  showDescription = false,
  clickable = true,
  className,
  showCompact = false,
}: {
  link: Link
  titleOnTop?: boolean
  imageCover?: boolean
  showEmptyThumbnail?: boolean
  showDescription?: boolean
  clickable?: boolean
  className?: string
  showCompact?: boolean
}): JSX.Element => {
  const { url: originalUrl, expandedUrl, title, thumbnail, description } = link
  const url = expandedUrl || originalUrl

  return (
    <div
      data-testid="link-preview"
      translate="no"
      className={clsx(
        styles.wrapper,
        { [styles.sideThumbnail]: !thumbnail },
        className,
      )}
    >
      {showCompact && (
        <CompactView
          url={url}
          title={title}
          titleOnTop={titleOnTop}
          clickable={clickable}
          description={showDescription ? description : undefined}
        />
      )}

      {!showCompact && (
        <FullView
          thumbnail={thumbnail}
          url={url}
          title={title}
          titleOnTop={titleOnTop}
          clickable={clickable}
          imageCover={imageCover}
          showEmptyThumbnail={showEmptyThumbnail}
          description={showDescription ? description : undefined}
        />
      )}
    </div>
  )
}

export { LinkPreview }
